import {Component, OnDestroy, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';

import {VehiclesService} from '../../../../../data/vehicles/vehicles.service';
import {ConfigurationModel, FeatureFlagEnum} from '../../../../../shared/models/configuration.model';
import {ConfigurationService} from '../../../../../configuration/configuration.service';
import {ToastService} from '../../../../../shared/services/toast.service';
import {VehicleCategoryModel} from '../../../../../shared/models/vehicle';
import {firstValueFrom, Subscription} from 'rxjs';
import {Router, RouterLink} from '@angular/router';
import {
  CardContentDirective,
  CardContentRawDirective
} from '../../../../../shared/components/card/card-content.directive';
import {CardComponent} from '../../../../../shared/components/card/card.component';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatListItem, MatListItemMeta, MatListItemTitle, MatNavList} from '@angular/material/list';
import {MatIcon} from '@angular/material/icon';
import {MatButton, MatIconButton} from '@angular/material/button';
import {RootRoute, SettingsRoute} from '../../../../../shared/models/angular-routing';
import {MatMenu, MatMenuContent, MatMenuItem, MatMenuTrigger} from '@angular/material/menu';
import {
  DialogSingleFieldEditComponent,
  SingleFieldEditData
} from '../dialogs-vehicle/dialog-single-field-edit.component';
import {ImportVehiclesDialogComponent} from '../add-or-import-vehicle-dialog/import-vehicles-dialog.component';
import {EditVehicleComponentInputData} from '../model/vehicle.types';
import {NgClass} from '@angular/common';
import {MatDivider} from '@angular/material/divider';
import {DialogConfirmDeleteVehicleCategoryComponent} from '../dialogs-vehicle-group/vehicle-group-dialog-components';

@Component({
  selector: 'app-vehicle-group-list',
  templateUrl: './vehicle-group-list.component.html',
  styleUrls: ['./vehicle-group-list.component.scss',
    '../../../../settings/settings-fields.scss',
    'vehicle-group-list.component.scss',
    '../../../settings-common.scss'
  ],
  imports: [
    CardComponent,
    CardContentDirective,
    MatProgressSpinner,
    MatIcon,
    MatListItem,
    MatButton,
    CardContentRawDirective,
    RouterLink,
    MatIconButton,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatListItemTitle,
    MatListItemMeta,
    MatMenuContent,
    NgClass,
    MatNavList,
    MatDivider,
  ]
})
export class VehicleGroupListComponent implements OnInit, OnDestroy {
  isLoading = true;
  isCartegraphEnabled = false;
  vehicleGroups: VehicleCategoryModel[] = [];
  RootRoute = RootRoute;
  SettingsRoute = SettingsRoute;
  private configurationSubscription: Subscription;
  private configuration: ConfigurationModel;

  constructor(public dialog: MatDialog,
              private readonly toastService: ToastService,
              private readonly vehicleService: VehiclesService,
              private readonly configurationService: ConfigurationService,
              private readonly router: Router,
  ) {
  }

  deleteGroup(group: VehicleCategoryModel) {
    const ref = this.dialog.open(DialogConfirmDeleteVehicleCategoryComponent, {
      data: group
    });

    firstValueFrom(ref.afterClosed()).then(result => {
      if (result) {
        this.vehicleService.deleteVehicleCategory(group.id).then(() => {
          this.reload();
          this.toastService.long('The group has been deleted');
        });
      }
    });
  }

  ngOnInit() {
    this.configurationSubscription = this.configurationService.sharedConfigurationModel.subscribe(model => {
      if (model) {
        this.configuration = model;
        this.isCartegraphEnabled = this.configuration.hasFeatureFlag(FeatureFlagEnum.CartegraphIntegration);
      }
      this.reload();
    });
  }

  ngOnDestroy() {
    this.configurationSubscription?.unsubscribe();
  }

  showAddNewVehicleGroupDialog() {
    const dialogRef = this.dialog
      .open<DialogSingleFieldEditComponent, SingleFieldEditData, VehicleCategoryModel>(DialogSingleFieldEditComponent, {
        width: '400px',
        data: {
          title: 'Add Vehicle Group',
          actionLabel: 'Add Group',
          fieldName: 'Group Name',
          value: '',
          action: async value => {
            const vehicleGroup = {
              name: value,
              title: value // change both, the name and the title
            } as VehicleCategoryModel;
            return await this.vehicleService.createVehicleCategory(vehicleGroup);
          }
        }
      });

    firstValueFrom(dialogRef.afterClosed()).then(result => {
      if (!result) {
        return;
      }
      this.router.navigate([`/${RootRoute.SETTINGS}`, SettingsRoute.MANAGE_VEHICLES, 'group', result.id])
        .then();
      this.toastService.long('Vehicle Group has been renamed.');
    });
  }

  changeGroupName(group: VehicleCategoryModel) {
    const dialogRef = this.dialog
      .open<DialogSingleFieldEditComponent, SingleFieldEditData, VehicleCategoryModel>(DialogSingleFieldEditComponent, {
        width: '400px',
        data: {
          title: 'Rename Vehicle Group',
          actionLabel: 'Confirm',
          fieldName: 'Group Name',
          value: group.name,
          action: async value => {
            const updatedGroup = {
              ...group,
              name: value,
              title: value // change both, the name and the title
            } as VehicleCategoryModel;
            return await firstValueFrom(this.vehicleService.updateVehicleCategory(group.id, updatedGroup));
          }
        } as SingleFieldEditData
      });

    firstValueFrom(dialogRef.afterClosed()).then(result => {
      if (!result) {
        return;
      }
      this.reload();
      this.toastService.long('Vehicle Group has been renamed.');
    });
  }

  importVehicles() {
    const dialogRef = this.dialog.open(ImportVehiclesDialogComponent, {
      width: '800px',
      data: {
        model: {},
        isImported: this.isCartegraphEnabled,
        configuration: this.configuration
      } as EditVehicleComponentInputData
    });
    firstValueFrom(dialogRef.afterClosed()).then(result => {
      if (!result) {
        return;
      }
      this.reload();
      this.toastService.long('Vehicles has been imported.');
    });
  }

  private reload() {
    this.isLoading = true;

    this.vehicleService.getVehicleCategories().then(groups => {
      this.vehicleGroups = groups;
    })
      .catch(error => {
        const msg = 'Error while loading data from server';
        this.toastService.long(msg);
        console.error(`${msg} :: ${error}`);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

}

